<script setup lang="ts">
import { Link, router, useForm } from "@inertiajs/vue3"
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { UiButton, UiFormLayout, UiStepper } from "@/components"
import { useNotifications } from "@/composables/useNotifications"
import SalesOrderStepBasicData from "./steps/SalesOrderStepBasicData.vue"
import SalesOrderStepCommercial from "./steps/SalesOrderStepCommercial.vue"
import SalesOrderStepItems from "./steps/SalesOrderStepItems.vue"
import SalesOrderStepProjectedSnapshot from "./steps/SalesOrderStepProjectedSnapshot.vue"

type SalesOrderItemPayload = {
  id?: number | null
  product_id: number | "" | null
  quantity: string
  unit_price_cents: string
  _destroy?: boolean
}

type SalesOrderInstallmentPayload = {
  due_date: string
  amount: string
}

type SalesOrderPayload = {
  id?: number | null
  company_id: number | ""
  customer_id: number | ""
  delivery_date: string
  estimated_tax_percent: string
  estimated_freight_cents: string
  packaging_percent: string
  overhead_percent: string
  indirect_costs_percent?: string
  payment_terms: string
  validity_date: string
  freight_mode: string
  commercial_notes: string
  installments: SalesOrderInstallmentPayload[]
  items: SalesOrderItemPayload[]
}

const props = defineProps<{
  title: string
  subtitle?: string
  salesOrder: SalesOrderPayload
  companies: Array<{ id: number; name: string }>
  customers: Array<{ label: string; value: number }>
  products: Array<{ label: string; value: number }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  sales_order: {
    company_id: props.salesOrder.company_id,
    customer_id: props.salesOrder.customer_id,
    delivery_date: props.salesOrder.delivery_date,
    estimated_tax_percent: props.salesOrder.estimated_tax_percent,
    estimated_freight_cents: props.salesOrder.estimated_freight_cents,
    packaging_percent: props.salesOrder.packaging_percent,
    overhead_percent: props.salesOrder.overhead_percent,
    indirect_costs_percent: props.salesOrder.indirect_costs_percent || props.salesOrder.overhead_percent,
    payment_terms: props.salesOrder.payment_terms,
    validity_date: props.salesOrder.validity_date,
    freight_mode: props.salesOrder.freight_mode,
    commercial_notes: props.salesOrder.commercial_notes,
    installments: props.salesOrder.installments.map((installment) => ({ ...installment })),
    items_attributes: props.salesOrder.items.map((item) => ({ ...item, _destroy: false })),
  },
})

const notify = useNotifications()
const customerOptions = ref(props.customers)
const productOptions = ref(props.products)
const currentStep = ref(0)
let customerSearchDebounce: number | null = null
let productSearchDebounce: number | null = null

const stepItems = [
  { key: "basic", label: "Dados basicos", description: "Empresa, cliente e entrega" },
  { key: "snapshot", label: "Snapshot", description: "Impostos e custos projetados" },
  { key: "items", label: "Itens", description: "Produtos, quantidades e valores" },
  { key: "commercial", label: "Comercial", description: "Pagamento, frete e parcelas" },
]

const companyOptions = computed(() => props.companies.map((company) => ({ label: company.name, value: company.id })))
const paymentOptions = [
  { label: "A vista", value: "a_vista" },
  { label: "PIX", value: "pix" },
  { label: "Boleto", value: "boleto" },
  { label: "Parcelamento", value: "parcelamento" },
]
const isInstallmentPayment = computed(() => form.sales_order.payment_terms === "parcelamento")
const isFirstStep = computed(() => currentStep.value === 0)
const isLastStep = computed(() => currentStep.value === stepItems.length - 1)
const visibleItems = computed(() => form.sales_order.items_attributes.filter((item) => !item._destroy))
const fieldError = (field: string) => props.errors?.[field]?.[0]

function itemError(index: number, field: string) {
  return props.errors?.[`sales_order_items.${index}.${field}`]?.[0] || props.errors?.[`items_attributes.${index}.${field}`]?.[0]
}

function installmentError(index: number, field: string) {
  return props.errors?.[`installments.${index}.${field}`]?.[0]
}

function onCompanyChange(companyId: number | "") {
  form.sales_order.company_id = companyId
  form.sales_order.customer_id = ""

  router.get("/sales_orders/new", { company_id: companyId || "" }, { preserveState: false })
}

function loadCustomerOptions(query = "") {
  if (!form.sales_order.company_id) {
    customerOptions.value = []
    return
  }

  const url = new URL("/sales_orders/customer_options", window.location.origin)
  url.searchParams.set("company_id", String(form.sales_order.company_id))
  if (query.trim()) url.searchParams.set("q", query.trim())

  fetch(url.toString(), {
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
    },
  })
    .then((response) => response.ok ? response.json() : { customers: [] })
    .then((data: { customers: Array<{ label: string; value: number }> }) => {
      customerOptions.value = data.customers || []
    })
}

function loadProductOptions(query = "") {
  if (!form.sales_order.company_id) {
    productOptions.value = []
    return
  }

  const url = new URL("/sales_orders/product_options", window.location.origin)
  url.searchParams.set("company_id", String(form.sales_order.company_id))
  if (query.trim()) url.searchParams.set("q", query.trim())

  fetch(url.toString(), {
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
    },
  })
    .then((response) => response.ok ? response.json() : { products: [] })
    .then((data: { products: Array<{ label: string; value: number }> }) => {
      productOptions.value = data.products || []
    })
}

function searchCustomers(query: string) {
  if (customerSearchDebounce) window.clearTimeout(customerSearchDebounce)

  customerSearchDebounce = window.setTimeout(() => {
    loadCustomerOptions(query)
  }, 250)
}

function searchProducts(query: string) {
  if (productSearchDebounce) window.clearTimeout(productSearchDebounce)

  productSearchDebounce = window.setTimeout(() => {
    loadProductOptions(query)
  }, 250)
}

onBeforeUnmount(() => {
  if (customerSearchDebounce) window.clearTimeout(customerSearchDebounce)
  if (productSearchDebounce) window.clearTimeout(productSearchDebounce)
})

watch(
  () => form.sales_order.payment_terms,
  (paymentTerms) => {
    if (paymentTerms === "parcelamento") {
      if (form.sales_order.installments.length === 0) {
        addInstallment()
      }
      return
    }

    form.sales_order.installments = []
  },
  { immediate: true }
)

function validateStep(stepIndex: number): string | null {
  if (stepIndex === 0) {
    if (!form.sales_order.company_id) return "Selecione uma empresa para continuar."
    if (!form.sales_order.customer_id) return "Selecione um cliente para continuar."
    if (!form.sales_order.delivery_date) return "Informe a data prevista de entrega para continuar."
  }

  if (stepIndex === 2) {
    if (visibleItems.value.length === 0) return "Adicione pelo menos um item no pedido."

    const hasInvalidItem = visibleItems.value.some((item) => {
      const quantity = Number(item.quantity)
      return !item.product_id || !Number.isFinite(quantity) || quantity <= 0
    })

    if (hasInvalidItem) return "Revise os itens: produto e quantidade valida sao obrigatorios."
  }

  return null
}

function goToStep(stepIndex: number) {
  if (stepIndex <= currentStep.value) {
    currentStep.value = stepIndex
    return
  }

  for (let index = currentStep.value; index < stepIndex; index += 1) {
    const validationMessage = validateStep(index)
    if (validationMessage) {
      notify.warning(validationMessage)
      currentStep.value = index
      return
    }
  }

  currentStep.value = stepIndex
}

function goNext() {
  if (isLastStep.value) return

  goToStep(currentStep.value + 1)
}

function goBack() {
  if (isFirstStep.value) return

  currentStep.value -= 1
}

function addItem() {
  form.sales_order.items_attributes.push({
    id: null,
    product_id: "",
    quantity: "1",
    unit_price_cents: "0",
    _destroy: false,
  })
}

function removeItem(index: number) {
  const item = form.sales_order.items_attributes[index]
  if (!item) return

  if (item.id) {
    item._destroy = true
    return
  }

  form.sales_order.items_attributes.splice(index, 1)
}

function addInstallment() {
  form.sales_order.installments.push({
    due_date: "",
    amount: "",
  })
}

function removeInstallment(index: number) {
  form.sales_order.installments.splice(index, 1)
}

function submit() {
  for (let index = 0; index < stepItems.length - 1; index += 1) {
    const validationMessage = validateStep(index)
    if (validationMessage) {
      notify.warning(validationMessage)
      currentStep.value = index
      return
    }
  }

  form.post("/sales_orders", {
    onSuccess: () => notify.success("Pedido de venda criado com sucesso"),
  })
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/sales_orders" back-label="Voltar para pedidos de venda">
    <form class="space-y-4" @submit.prevent="submit">
      <UiStepper :steps="stepItems" :current-step="currentStep" @update:current-step="goToStep" />

      <SalesOrderStepBasicData
        v-if="currentStep === 0"
        :form-state="form.sales_order"
        :company-options="companyOptions"
        :customer-options="customerOptions"
        :field-error="fieldError"
        @company-change="onCompanyChange"
        @search-customers="searchCustomers"
      />

      <SalesOrderStepProjectedSnapshot v-if="currentStep === 1" :form-state="form.sales_order" :field-error="fieldError" />

      <SalesOrderStepItems
        v-if="currentStep === 2"
        :form-state="form.sales_order"
        :product-options="productOptions"
        :item-error="itemError"
        :field-error="fieldError"
        @add-item="addItem"
        @remove-item="removeItem"
        @search-products="searchProducts"
      />

      <SalesOrderStepCommercial
        v-if="currentStep === 3"
        :form-state="form.sales_order"
        :field-error="fieldError"
        :installment-error="installmentError"
        :payment-options="paymentOptions"
        :is-installment-payment="isInstallmentPayment"
        @add-installment="addInstallment"
        @remove-installment="removeInstallment"
      />

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="button" variant="secondary" :disabled="isFirstStep" @click="goBack">Voltar etapa</UiButton>

        <UiButton v-if="!isLastStep" type="button" @click="goNext">Proxima etapa</UiButton>
        <UiButton v-else type="submit" :disabled="form.processing">Criar pedido</UiButton>

        <Link href="/sales_orders" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>

